/**
 * Camada de acesso a dados sobre o Supabase. Renomeado de `firebaseService`
 * pós-migração — `dataService` reflete o que ele é hoje.
 */
import { supabase } from '../supabase';
import type {
  Partner,
  AppSettings,
  FakeSession,
  Indication,
  Banner,
  Notification,
  Avatar,
  CustomizationItem,
  MarketingEventConfig,
  MarketingAutomationConfig,
  CampaignRun,
  CampaignRunInput,
} from './types';
import {
  DEFAULT_CARD_IMAGE,
  CITIES,
  CATEGORIES,
  DEFAULT_LOGIN_ONBOARDING_SLIDES,
  normalizePartnerCategory,
} from '../constants';

// ---------------------------------------------------------------------------
// Mappers Postgres (snake_case) ↔ TypeScript (camelCase)
// ---------------------------------------------------------------------------

/**
 * Row genérico vindo do Supabase. PostgrestResponse retorna o shape dinâmico
 * baseado no `select()` da query — usar tipo específico aqui exigiria duplicar
 * cada schema da tabela, com baixo ganho prático. Os mappers abaixo validam
 * cada campo em tempo de uso.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DbRow = Record<string, any>;

function rowToPartner(r: DbRow): Partner {
  return {
    id: r.id,
    name: r.name,
    category: r.category,
    city: r.city,
    discount: r.discount,
    address: r.address ?? '',
    description: r.description ?? '',
    rules: r.rules ?? '',
    instagram: r.instagram ?? '',
    icon: r.icon ?? '',
    logoUrl: r.logo_url ?? '',
    coverUrl: r.cover_url ?? '',
    highlight: !!r.highlight,
    trending: !!r.trending,
    active: r.active !== false,
    usageCount: r.usage_count ?? 0,
    viewCount: r.view_count ?? 0,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
    serviceModality: r.service_modality ?? undefined,
    validationMethod: r.validation_method ?? undefined,
    validationLink: r.validation_link ?? undefined,
    locationLat: r.location_lat ?? undefined,
    locationLng: r.location_lng ?? undefined,
    locationRadiusMeters: r.location_radius_meters ?? undefined,
    allowDeclaredValue: r.allow_declared_value ?? undefined,
    discountCode: r.discount_code ?? undefined,
  };
}

function partnerToRow(p: Partial<Partner>): DbRow {
  const out: DbRow = {};
  if (p.name !== undefined) out.name = p.name;
  if (p.category !== undefined) out.category = normalizePartnerCategory(p.category);
  if (p.city !== undefined) out.city = p.city;
  if (p.discount !== undefined) out.discount = p.discount;
  if (p.address !== undefined) out.address = p.address;
  if (p.description !== undefined) out.description = p.description;
  if (p.rules !== undefined) out.rules = p.rules;
  if (p.instagram !== undefined) out.instagram = p.instagram;
  if (p.icon !== undefined) out.icon = p.icon;
  if (p.logoUrl !== undefined) out.logo_url = p.logoUrl;
  if (p.coverUrl !== undefined) out.cover_url = p.coverUrl;
  if (p.highlight !== undefined) out.highlight = p.highlight;
  if (p.trending !== undefined) out.trending = p.trending;
  if (p.active !== undefined) out.active = p.active;
  if (p.serviceModality !== undefined) out.service_modality = p.serviceModality;
  if (p.validationMethod !== undefined) out.validation_method = p.validationMethod;
  if (p.validationLink !== undefined) out.validation_link = p.validationLink;
  if (p.locationLat !== undefined) out.location_lat = p.locationLat;
  if (p.locationLng !== undefined) out.location_lng = p.locationLng;
  if (p.locationRadiusMeters !== undefined) out.location_radius_meters = p.locationRadiusMeters;
  if (p.allowDeclaredValue !== undefined) out.allow_declared_value = p.allowDeclaredValue;
  if (p.discountCode !== undefined) out.discount_code = p.discountCode;
  return out;
}

function rowToBanner(r: DbRow): Banner {
  return {
    id: r.id,
    title: r.title ?? '',
    imageUrl: r.image_url,
    linkUrl: r.link_url ?? '',
    isActive: r.is_active ?? true,
    createdAt: r.created_at,
  };
}

function rowToAvatar(r: DbRow): Avatar {
  return {
    id: r.id,
    name: r.name,
    imageUrl: r.image_url,
    active: r.active ?? true,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

function rowToCustomizationItem(r: DbRow): CustomizationItem {
  return {
    docId: r.id, // mantém compat: docId == id no Postgres
    id: r.id,
    type: r.type,
    name: r.name,
    description: r.description ?? '',
    active: r.active ?? true,
    displayOrder: r.display_order ?? 0,
    previewConfig: r.preview_config ?? '',
    unlockType: r.unlock_type,
    unlockValue: parseUnlockValue(r.unlock_type, r.unlock_value),
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

function parseUnlockValue(
  type: string,
  raw: string | null | undefined,
): string | number | undefined {
  if (raw == null || raw === '') return type === 'free' ? '' : undefined;
  if (type === 'minPoints') {
    const n = Number(raw);
    return Number.isNaN(n) ? raw : n;
  }
  return raw;
}

function rowToNotification(r: DbRow): Notification {
  return {
    id: r.id,
    title: r.title,
    message: r.message,
    targetAudience: r.target_audience,
    createdAt: r.created_at,
  };
}

function rowToIndication(r: DbRow): Indication {
  return {
    id: r.id,
    companyName: r.company_name,
    instagram: r.instagram ?? '',
    phone: r.phone ?? '',
    observation: r.observation ?? '',
    createdAt: r.created_at,
    userId: r.user_id ?? null,
  };
}

function rowToAppSettings(r: DbRow | null): AppSettings {
  return {
    mainImage: r?.main_image || DEFAULT_CARD_IMAGE,
    levelImages: r?.level_images || {},
    availableCities: r?.available_cities?.length ? r.available_cities : CITIES,
    availableCategories: CATEGORIES,
    loginOnboardingSlides: r?.login_onboarding_slides?.length
      ? r.login_onboarding_slides
      : DEFAULT_LOGIN_ONBOARDING_SLIDES,
    partnerSupportWhatsapp: r?.partner_support_whatsapp || '+55 51 98643-0383',
    // Social handles — defaults conhecidos do produto. Permite que o card de IG
    // apareça pros alunos imediatamente, mesmo sem admin ter configurado.
    // Admin pode override em Marketing → Configurações.
    sixInstagram: r?.six_instagram || 'sixformaturas',
    alphaInstagram: r?.alpha_instagram || 'alphasixformaturas',
    sixInstagramCaxias: r?.six_instagram_caxias || 'sixformaturassg',
  };
}

// ---------------------------------------------------------------------------
// Storage path → bucket+key.
// Convenções herdadas do Firebase Storage; mapeamento documentado em
// supabase/storage.sql.
// ---------------------------------------------------------------------------

const FLAT_BUCKETS = new Set(['partners', 'banners', 'avatars', 'branding', 'settings']);

function splitStoragePath(path: string): { bucket: string; key: string } {
  // `users/{uid}/avatar/{file}` (legado) → `user-avatars/{uid}/{file}`
  const userMatch = path.match(/^users\/([^/]+)\/avatar\/(.+)$/);
  if (userMatch) return { bucket: 'user-avatars', key: `${userMatch[1]}/${userMatch[2]}` };

  // `admin/{file}` → `admin-docs/{file}`
  if (path.startsWith('admin/')) return { bucket: 'admin-docs', key: path.slice('admin/'.length) };

  // Buckets flat: primeira pasta é o bucket, resto é a key.
  const slash = path.indexOf('/');
  if (slash > 0) {
    const top = path.slice(0, slash);
    if (FLAT_BUCKETS.has(top)) return { bucket: top, key: path.slice(slash + 1) };
  }
  // Fallback: bucket settings (compat).
  return { bucket: 'settings', key: path };
}

async function dataUrlToBlob(dataUrl: string): Promise<Blob> {
  const res = await fetch(dataUrl);
  return await res.blob();
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function getCurrentUserId(): Promise<string | null> {
  const { data } = await supabase.auth.getUser();
  return data.user?.id ?? null;
}

function unwrap<T>(data: T | null, error: { message: string } | null, op: string): T {
  if (error) {
    console.error(`[dataService] ${op}:`, error.message);
    throw error;
  }
  if (data == null) throw new Error(`${op}: empty response`);
  return data;
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

export const dataService = {
  // -------------------- Partners --------------------
  /**
   * Lista parceiros. Por padrão só retorna `active != false` — usuário final
   * não vê parceiros desativados pelo admin. Pra admin, passe
   * `{ includeInactive: true }` pra ver todos.
   */
  async getPartners(options?: { includeInactive?: boolean }): Promise<Partner[]> {
    let query = supabase.from('partners').select('*');
    if (!options?.includeInactive) {
      // `active != false` cobre tanto `true` quanto `null` (parceiros legados
      // sem o campo). Só filtra os explicitamente desativados.
      query = query.neq('active', false);
    }
    const { data, error } = await query;
    return unwrap(data, error, 'getPartners').map(rowToPartner);
  },

  async getPartnerById(id: string | number): Promise<Partner | null> {
    const { data, error } = await supabase
      .from('partners')
      .select('*')
      .eq('id', String(id))
      .maybeSingle();
    if (error) {
      console.error('[dataService] getPartnerById:', error.message);
      throw error;
    }
    return data ? rowToPartner(data) : null;
  },

  async createPartner(partner: Omit<Partner, 'id' | 'createdAt' | 'updatedAt'>): Promise<Partner> {
    const { data, error } = await supabase
      .from('partners')
      .insert(partnerToRow(partner))
      .select()
      .single();
    return rowToPartner(unwrap(data, error, 'createPartner'));
  },

  async updatePartner(id: string | number, updates: Partial<Partner>): Promise<Partner> {
    const { data, error } = await supabase
      .from('partners')
      .update(partnerToRow(updates))
      .eq('id', String(id))
      .select()
      .single();
    return rowToPartner(unwrap(data, error, 'updatePartner'));
  },

  async deletePartner(id: string | number): Promise<void> {
    const { error } = await supabase.from('partners').delete().eq('id', String(id));
    if (error) throw error;
  },

  async incrementPartnerViewCount(id: string | number): Promise<void> {
    const { error } = await supabase.rpc('increment_partner_counter', {
      p_partner_id: String(id),
      p_field: 'view_count',
      p_delta: 1,
    });
    if (error) throw error;
  },

  async togglePartnerHighlight(id: string | number): Promise<Partner> {
    const partner = await this.getPartnerById(id);
    if (!partner) throw new Error('Partner not found');
    return this.updatePartner(id, { highlight: !partner.highlight });
  },

  async togglePartnerTrending(id: string | number): Promise<Partner> {
    const partner = await this.getPartnerById(id);
    if (!partner) throw new Error('Partner not found');
    return this.updatePartner(id, { trending: !partner.trending });
  },

  // -------------------- AppSettings --------------------
  async getAppSettings(): Promise<AppSettings> {
    const { data, error } = await supabase
      .from('settings')
      .select('*')
      .eq('id', 'app')
      .maybeSingle();
    if (error) {
      console.error('[dataService] getAppSettings:', error.message);
      throw error;
    }
    return rowToAppSettings(data);
  },

  async updateMainImage(url: string): Promise<AppSettings> {
    const { error } = await supabase.from('settings').update({ main_image: url }).eq('id', 'app');
    if (error) throw error;
    return this.getAppSettings();
  },

  async updateLevelImages(levelImages: Record<string, string>): Promise<AppSettings> {
    const { error } = await supabase
      .from('settings')
      .update({ level_images: levelImages })
      .eq('id', 'app');
    if (error) throw error;
    return this.getAppSettings();
  },

  async updateLoginOnboardingSlides(
    loginOnboardingSlides: AppSettings['loginOnboardingSlides'],
  ): Promise<AppSettings> {
    const { error } = await supabase
      .from('settings')
      .update({ login_onboarding_slides: loginOnboardingSlides })
      .eq('id', 'app');
    if (error) throw error;
    return this.getAppSettings();
  },

  async updatePartnerSupportWhatsapp(partnerSupportWhatsapp: string): Promise<AppSettings> {
    const { error } = await supabase
      .from('settings')
      .update({ partner_support_whatsapp: partnerSupportWhatsapp })
      .eq('id', 'app');
    if (error) throw error;
    return this.getAppSettings();
  },

  /**
   * Atualiza os handles do Instagram (Six + Alpha). Best-effort: se as colunas
   * `six_instagram` / `alpha_instagram` não existirem no schema ainda, falha
   * silenciosamente e o caller deve persistir em localStorage como fallback.
   */
  async updateSocialHandles(handles: {
    sixInstagram?: string;
    alphaInstagram?: string;
    sixInstagramCaxias?: string;
  }): Promise<AppSettings> {
    const payload: Record<string, string> = {};
    if (handles.sixInstagram !== undefined) payload.six_instagram = handles.sixInstagram;
    if (handles.alphaInstagram !== undefined)
      payload.alpha_instagram = handles.alphaInstagram;
    if (handles.sixInstagramCaxias !== undefined)
      payload.six_instagram_caxias = handles.sixInstagramCaxias;
    const { error } = await supabase.from('settings').update(payload).eq('id', 'app');
    if (error) {
      // Schema pode não ter essas colunas ainda — não trava o fluxo.
      console.warn(
        '[dataService] updateSocialHandles falhou (esperado se schema sem colunas):',
        error.message,
      );
    }
    return this.getAppSettings();
  },

  async resetSeedData(): Promise<void> {
    console.warn('resetSeedData chamado em ambiente Supabase — não implementado.');
  },

  // -------------------- Auth fake (legacy noop) --------------------
  async loginFake(_username: string, _password: string): Promise<FakeSession> {
    return { isLoggedIn: true, userName: 'Admin' };
  },

  async logoutFake(): Promise<void> {
    /* noop */
  },

  async getSession(): Promise<FakeSession> {
    return { isLoggedIn: false, userName: '' };
  },

  // -------------------- Indications --------------------
  async getIndications(): Promise<Indication[]> {
    const { data, error } = await supabase
      .from('indications')
      .select('*')
      .order('created_at', { ascending: false });
    return unwrap(data, error, 'getIndications').map(rowToIndication);
  },

  async addIndication(
    indication: Omit<Indication, 'id' | 'createdAt' | 'userId'>,
  ): Promise<Indication> {
    const userId = await getCurrentUserId();
    const { data, error } = await supabase
      .from('indications')
      .insert({
        company_name: indication.companyName,
        instagram: indication.instagram || null,
        phone: indication.phone || null,
        observation: indication.observation || null,
        user_id: userId,
      })
      .select()
      .single();
    return rowToIndication(unwrap(data, error, 'addIndication'));
  },

  async deleteIndication(id: string): Promise<void> {
    const { error } = await supabase.from('indications').delete().eq('id', id);
    if (error) throw error;
  },

  // -------------------- Banners --------------------
  async getBanners(options?: { activeOnly?: boolean }): Promise<Banner[]> {
    const activeOnly = options?.activeOnly ?? true;
    let qb = supabase.from('banners').select('*').order('display_order', { ascending: true });
    if (activeOnly) qb = qb.eq('is_active', true);
    const { data, error } = await qb;
    return unwrap(data, error, 'getBanners').map(rowToBanner);
  },

  async createBanner(banner: Omit<Banner, 'id' | 'createdAt'>): Promise<Banner> {
    const { data, error } = await supabase
      .from('banners')
      .insert({
        title: banner.title || null,
        image_url: banner.imageUrl,
        link_url: banner.linkUrl || null,
        is_active: banner.isActive ?? true,
      })
      .select()
      .single();
    return rowToBanner(unwrap(data, error, 'createBanner'));
  },

  async updateBanner(id: string, updates: Partial<Banner>): Promise<void> {
    const row: DbRow = {};
    if (updates.title !== undefined) row.title = updates.title;
    if (updates.imageUrl !== undefined) row.image_url = updates.imageUrl;
    if (updates.linkUrl !== undefined) row.link_url = updates.linkUrl;
    if (updates.isActive !== undefined) row.is_active = updates.isActive;
    const { error } = await supabase.from('banners').update(row).eq('id', id);
    if (error) throw error;
  },

  async deleteBanner(id: string): Promise<void> {
    const { error } = await supabase.from('banners').delete().eq('id', id);
    if (error) throw error;
  },

  // -------------------- Avatars --------------------
  async getAvatars(includeInactive = false): Promise<Avatar[]> {
    let qb = supabase.from('avatars').select('*').order('created_at', { ascending: true });
    if (!includeInactive) qb = qb.eq('active', true);
    const { data, error } = await qb;
    return unwrap(data, error, 'getAvatars').map(rowToAvatar);
  },

  async createAvatar(avatar: Omit<Avatar, 'id' | 'createdAt' | 'updatedAt'>): Promise<Avatar> {
    const { data, error } = await supabase
      .from('avatars')
      .insert({
        name: avatar.name,
        image_url: avatar.imageUrl,
        active: avatar.active ?? true,
      })
      .select()
      .single();
    return rowToAvatar(unwrap(data, error, 'createAvatar'));
  },

  async updateAvatar(id: string, updates: Partial<Avatar>): Promise<void> {
    const row: DbRow = {};
    if (updates.name !== undefined) row.name = updates.name;
    if (updates.imageUrl !== undefined) row.image_url = updates.imageUrl;
    if (updates.active !== undefined) row.active = updates.active;
    const { error } = await supabase.from('avatars').update(row).eq('id', id);
    if (error) throw error;
  },

  async deleteAvatar(id: string): Promise<void> {
    const { error } = await supabase.from('avatars').delete().eq('id', id);
    if (error) throw error;
  },

  // -------------------- Customization items --------------------
  async getCustomizationItems(includeInactive = false): Promise<CustomizationItem[]> {
    let qb = supabase
      .from('customization_items')
      .select('*')
      .order('display_order', { ascending: true });
    if (!includeInactive) qb = qb.eq('active', true);
    const { data, error } = await qb;
    const items = unwrap(data, error, 'getCustomizationItems').map(rowToCustomizationItem);
    return items.sort((a, b) => {
      const orderDiff = (a.displayOrder || 0) - (b.displayOrder || 0);
      if (orderDiff !== 0) return orderDiff;
      return `${a.type}_${a.id}`.localeCompare(`${b.type}_${b.id}`);
    });
  },

  async createCustomizationItem(
    item: Omit<CustomizationItem, 'docId' | 'createdAt' | 'updatedAt'>,
  ): Promise<CustomizationItem> {
    // Mantém a convenção antiga: PK é `${type}_${id}` (composto).
    const compositeId = `${item.type}_${String(item.id).trim()}`;
    const unlockValue = item.unlockValue == null ? null : String(item.unlockValue).trim();
    const { data, error } = await supabase
      .from('customization_items')
      .insert({
        id: compositeId,
        type: item.type,
        name: String(item.name ?? '').trim(),
        description: String(item.description ?? '').trim(),
        active: !!item.active,
        display_order: Number(item.displayOrder) || 1,
        preview_config: item.previewConfig ? String(item.previewConfig).trim() : compositeId,
        unlock_type: item.unlockType,
        unlock_value: item.unlockType === 'free' ? '' : unlockValue,
      })
      .select()
      .single();
    return rowToCustomizationItem(unwrap(data, error, 'createCustomizationItem'));
  },

  async updateCustomizationItem(docId: string, updates: Partial<CustomizationItem>): Promise<void> {
    const row: DbRow = {};
    if (updates.type !== undefined) row.type = updates.type;
    if (updates.name !== undefined) row.name = String(updates.name).trim();
    if (updates.description !== undefined) row.description = String(updates.description).trim();
    if (updates.active !== undefined) row.active = !!updates.active;
    if (updates.displayOrder !== undefined) row.display_order = Number(updates.displayOrder) || 1;
    if (updates.previewConfig !== undefined)
      row.preview_config = String(updates.previewConfig).trim() || docId;
    if (updates.unlockType !== undefined) row.unlock_type = updates.unlockType;
    if (updates.unlockValue !== undefined) {
      row.unlock_value =
        updates.unlockType === 'free' || updates.unlockValue == null
          ? ''
          : String(updates.unlockValue).trim();
    }
    const { error } = await supabase.from('customization_items').update(row).eq('id', docId);
    if (error) throw error;
  },

  async deleteCustomizationItem(docId: string): Promise<void> {
    const { error } = await supabase.from('customization_items').delete().eq('id', docId);
    if (error) throw error;
  },

  // -------------------- Storage --------------------
  async uploadImage(fileOrBase64: File | string, path: string): Promise<string> {
    const { bucket, key } = splitStoragePath(path);
    let blob: Blob;
    let contentType: string | undefined;
    if (typeof fileOrBase64 === 'string') {
      blob = await dataUrlToBlob(fileOrBase64);
      contentType = blob.type || undefined;
    } else {
      blob = fileOrBase64;
      contentType = fileOrBase64.type;
    }
    const { error } = await supabase.storage.from(bucket).upload(key, blob, {
      upsert: true,
      contentType,
    });
    if (error) throw error;
    const { data } = supabase.storage.from(bucket).getPublicUrl(key);
    return data.publicUrl;
  },

  // -------------------- Notifications --------------------
  async getNotifications(): Promise<Notification[]> {
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .order('created_at', { ascending: false });
    return unwrap(data, error, 'getNotifications').map(rowToNotification);
  },

  async createNotification(
    notification: Omit<Notification, 'id' | 'createdAt'>,
  ): Promise<Notification> {
    const createdBy = await getCurrentUserId();
    const { data, error } = await supabase
      .from('notifications')
      .insert({
        title: notification.title,
        message: notification.message,
        target_audience: notification.targetAudience,
        created_by: createdBy,
      })
      .select()
      .single();
    return rowToNotification(unwrap(data, error, 'createNotification'));
  },

  async deleteNotification(id: string): Promise<void> {
    const { error } = await supabase.from('notifications').delete().eq('id', id);
    if (error) throw error;
  },

  async getNotificationReads(userId: string): Promise<string[]> {
    const { data, error } = await supabase
      .from('notification_reads')
      .select('notification_id')
      .eq('user_id', userId);
    if (error) throw error;
    return (data ?? []).map((r) => r.notification_id);
  },

  async markNotificationAsRead(userId: string, notificationId: string): Promise<void> {
    const { error } = await supabase.from('notification_reads').upsert(
      {
        user_id: userId,
        notification_id: notificationId,
      },
      { onConflict: 'user_id,notification_id' },
    );
    if (error) throw error;
  },

  // -------------------- Marketing (events + automations + runs) ------------
  //
  // Estratégia: Supabase primary, com tolerância a schema ausente (retorna
  // arrays vazios sem throw). MarketingAdmin combina com localStorage cache
  // pra resiliência durante a migração.
  //
  async getMarketingEvents(): Promise<MarketingEventConfig[]> {
    const { data, error } = await supabase
      .from('marketing_events')
      .select('id, event_date, logo_url, updated_at');
    if (error) {
      // Schema pode não estar aplicado ainda — silencia.
      console.warn('[dataService] getMarketingEvents:', error.message);
      return [];
    }
    return (data ?? []).map((r) => ({
      id: r.id as string,
      eventDate: (r.event_date as string | null) ?? null,
      logoUrl: (r.logo_url as string | null) ?? null,
      updatedAt: (r.updated_at as string | undefined) ?? undefined,
    }));
  },

  async upsertMarketingEvent(
    id: string,
    patch: { eventDate?: string | null; logoUrl?: string | null },
  ): Promise<MarketingEventConfig | null> {
    const userId = await getCurrentUserId();
    const payload: Record<string, unknown> = { id };
    if (patch.eventDate !== undefined) payload.event_date = patch.eventDate;
    if (patch.logoUrl !== undefined) payload.logo_url = patch.logoUrl;
    if (userId) payload.updated_by = userId;
    const { data, error } = await supabase
      .from('marketing_events')
      .upsert(payload, { onConflict: 'id' })
      .select('id, event_date, logo_url, updated_at')
      .single();
    if (error) {
      console.warn('[dataService] upsertMarketingEvent:', error.message);
      return null;
    }
    return {
      id: data.id as string,
      eventDate: (data.event_date as string | null) ?? null,
      logoUrl: (data.logo_url as string | null) ?? null,
      updatedAt: (data.updated_at as string | undefined) ?? undefined,
    };
  },

  async getMarketingAutomations(): Promise<MarketingAutomationConfig[]> {
    const { data, error } = await supabase
      .from('marketing_automations')
      .select('id, enabled, updated_at');
    if (error) {
      console.warn('[dataService] getMarketingAutomations:', error.message);
      return [];
    }
    return (data ?? []).map((r) => ({
      id: r.id as string,
      enabled: Boolean(r.enabled),
      updatedAt: (r.updated_at as string | undefined) ?? undefined,
    }));
  },

  async upsertMarketingAutomation(id: string, enabled: boolean): Promise<void> {
    const userId = await getCurrentUserId();
    const payload: Record<string, unknown> = { id, enabled };
    if (userId) payload.updated_by = userId;
    const { error } = await supabase
      .from('marketing_automations')
      .upsert(payload, { onConflict: 'id' });
    if (error) {
      console.warn('[dataService] upsertMarketingAutomation:', error.message);
    }
  },

  async logCampaignRun(input: CampaignRunInput): Promise<void> {
    const userId = await getCurrentUserId();
    const payload: Record<string, unknown> = {
      trigger_source: input.triggerSource,
      template_id: input.templateId,
      automation_id: input.automationId ?? null,
      title: input.title,
      message: input.message,
      audience: input.audience,
      city_filter: input.cityFilter ?? null,
      included_push: input.includedPush ?? false,
      push_sent_count: input.pushSentCount ?? 0,
      push_failed_count: input.pushFailedCount ?? 0,
      triggered_by: input.triggerSource === 'manual' ? userId : null,
    };
    const { error } = await supabase.from('marketing_campaign_runs').insert(payload);
    if (error) {
      // Não trava o fluxo do disparo — só perde o audit log.
      console.warn('[dataService] logCampaignRun:', error.message);
    }
  },

  async getCampaignRuns(limit = 50): Promise<CampaignRun[]> {
    const { data, error } = await supabase
      .from('marketing_campaign_runs')
      .select(
        'id, created_at, trigger_source, template_id, automation_id, title, message, audience, city_filter, included_push, push_sent_count, push_failed_count, triggered_by',
      )
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) {
      console.warn('[dataService] getCampaignRuns:', error.message);
      return [];
    }
    return (data ?? []).map((r) => ({
      id: r.id as string,
      createdAt: r.created_at as string,
      triggerSource: r.trigger_source as CampaignRun['triggerSource'],
      templateId: r.template_id as string,
      automationId: (r.automation_id as string | null) ?? null,
      title: r.title as string,
      message: r.message as string,
      audience: r.audience as string,
      cityFilter: (r.city_filter as string | null) ?? null,
      includedPush: Boolean(r.included_push),
      pushSentCount: (r.push_sent_count as number) ?? 0,
      pushFailedCount: (r.push_failed_count as number) ?? 0,
      triggeredBy: (r.triggered_by as string | null) ?? null,
    }));
  },
};
